import type {FunctionComponent} from 'react';
import {useTranslation} from '@app/hooks/use-translation';

import {LandingHeading, LandingParagraph} from '@tempo/landing';
import {Flex, VStack, Link} from '@tempo/core';
import {Image} from '@app/components/Image';
import {useIsLocale} from '@app/hooks/use-is-locale';
import GridSection from '@app/components/layout/GridSection';
import type {Config, Item} from '@app/components/layout/GridSection';
import LinkNext from 'next/link';
import {LandingIllustrationSection} from '@app/components/layout/LandingIllustrationSection';

export const Partner: FunctionComponent = () => {
	const {t} = useTranslation({ns: ['hifi', 'common']});
	const visuals_for_teufel = useIsLocale(['DE', 'CH', 'AT']); //TODO: CH ? AT ??
	const visuals_for_japan = useIsLocale(['JA']); // TODO: it was JP but i changed to JA
	const visuals_for_googleassistant = useIsLocale([
		'FR',
		'DE',
		'GB',
		'AU',
		'US',
		'IT',
		'MX',
		'SP',
	]); //TODO same here
	const visuals_for_amazon = useIsLocale([
		'US',
		'AU',
		'CA',
		'DE',
		'UK',
		'FR',
		'IT',
		'SP',
		'MX',
	]); //TODO : same here

	const labelGrid: Config[] = [
		{
			items: [
				{
					href: '/explore/devices/homepod',
					src: 'ac8fcba6d75a93dd8ae1e7520ddfaf93.png',
					title: 'Apple HomePod',
				},
				{
					href: '/explore/devices/bang',
					src: '97c61bb438f7e850d61c6e5305fbc5fc.png',
					title: 'Bang & Olufsen',
				},
				{
					href: '/explore/devices/bluesound',
					src: 'aedab5318080743abf9277684aa1604b.png',
					title: 'Bluesound',
				},
				{
					href: '/explore/devices/control4',
					src: '5eada44758aef3ae8724ceb48af21e59.png',
					title: 'Control 4',
				},
				{
					href: '/explore/devices/yamaha',
					src: '88dba780a0f1201ee33a4c6b0e8283f9.png',
					title: 'Yamaha',
				},
				...(visuals_for_teufel
					? [
							{
								href: '/explore/devices/teufel',
								src: '62127be90ebd41c8f4fd5f529f7670b5.png',
								title: 'Teufel',
							},
						]
					: []),
				{
					href: '/explore/devices/sonyaudio',
					src: '29b6d1c42f631b0c9871d937eec39014.png',
					title: 'Sony',
				},
				{
					href: '/explore/devices/sonos',
					src: '2425cda40dcd9fbf5810b0b10bbe21c7.png',
					title: 'Sonos',
				},
				{
					href: '/explore/devices/simaudio',
					src: '1441e994075480d1a39fbf907b48769d.png',
					title: 'Moon by Simaudio',
				},
				{
					href: '/explore/devices/samsung',
					src: 'ff60bff3d2c3db4c39315ce1d7cc0f15.png',
					title: 'Samsung',
				},
				...(visuals_for_japan
					? [
							{
								href: '/explore/devices/pioneer',
								src: '5625a46545097ba10f80a9fd6200d598.png',
								title: 'Pioneer',
							},
						]
					: []),
				{
					href: '/explore/devices/onkyo',
					src: 'bdf9f7673d229ba545f74d5df6e0669f.png',
					title: 'Onkyo',
				},
				{
					href: '/explore/devices/lgaudio',
					src: 'd18ab81eb780c4862df573d583718d98.png',
					title: 'LG',
				},
				{
					href: '/explore/devices/harman',
					src: '28fd9c21b2b1ee4b28ddb3bd4d8fc297.png',
					title: 'Harman/Kardon',
				},
				{
					href: '/explore/devices/googlehome',
					src: '2e5e11cb252055afeb832320c504a3a5.png',
					title: 'Google',
				},
				{
					href: '/explore/devices/devialet',
					src: '595cf3af21caeeb5539e2be89485094c.png',
					title: 'Devialet',
				},
				{href: '/explore/devices/soundsystems', cardTitle: t('En savoir plus')},
			] as Item[],
		},
	];

	return (
		<LandingIllustrationSection
			imageContained
			description={
				<>
					<LandingHeading as="h2">
						{t("La perfection s'invite chez vous")}
					</LandingHeading>
					<LandingParagraph
						dangerouslySetInnerHTML={{
							__html: t(
								'deezerhifilandingpage_text_flacmusicexceptionalsoundsystems_web',
							),
						}}
					/>
				</>
			}
			image={
				<VStack maxWidth="5xl" width="100%">
					<GridSection configs={labelGrid} />

					<Flex flexDirection="column" pt={8}>
						<LandingParagraph mb={10}>
							{t('hifilandingpage_text_availableondevicessupporting_web')}
						</LandingParagraph>
						<Flex
							flexWrap="wrap"
							flexDirection="row"
							justifyContent="center"
							alignItems="center"
							my={-8}
						>
							{visuals_for_amazon && (
								<Link
									as={LinkNext}
									m={4}
									href=""
									opacity="0.4"
									_hover={{opacity: '1'}}
								>
									<Image
										width={202}
										height={37}
										src="15e8ca35b25c4f3961f130f1e60d10e3.png"
										alt="amazon alexa"
									/>
								</Link>
							)}
							<Link
								as={LinkNext}
								m={4}
								href=""
								opacity="0.4"
								_hover={{opacity: '1'}}
							>
								<Image
									width={186}
									height={37}
									src="258196716ab36959e1798137404ba4ba.png"
									alt="chromecast"
								/>
							</Link>
							{visuals_for_googleassistant && (
								<Link
									as={LinkNext}
									m={4}
									href=""
									opacity="0.4"
									_hover={{opacity: '1'}}
								>
									<Image
										width={232}
										height={38}
										src="0603197d227d8bd3281f61f09e2f5b06.png"
										alt="Google Assistant"
									/>
								</Link>
							)}
						</Flex>
					</Flex>
				</VStack>
			}
		/>
	);
};
